/**
 * موافقة الكوكيز — مصدر حقيقة واحد.
 * الكوكيز الأساسيّة (سلّة/لغة/سوق/جلسة) لا تحتاج موافقة.
 * التحليلات (GA4/Clarity) والإعلانات (Meta/TikTok/Snap/Google Ads) تحتاج موافقة صريحة.
 */
import { noteConsentTransition } from "./consentTransitions";
export type ConsentValue = {
  analytics: boolean;
  ads: boolean;
  /** ISO timestamp للموافقة — للتوثيق */
  at: string;
  /** نسخة نصّ سياسة الكوكيز عند الموافقة */
  version: string;
};

export const COOKIE_CONSENT_VERSION = "1.0";
const STORAGE_KEY = "lecmo_cookie_consent_v1";
export const CONSENT_EVENT = "lecmo:cookie-consent";
export const COOKIE_SETTINGS_EVENT = "lecmo:cookie-settings";
/** سنة كاملة */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * نطاق الكوكي: `.lecmo.com` ليشمل الجذر وكلّ النطاقات الفرعيّة
 * (`new.` · `www.`) فلا تضيع الموافقة عند تبديل الدومين.
 * على localhost أو نطاق المعاينة نتركه بلا domain.
 */
function cookieDomain(): string {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  if (host === "lecmo.com" || host.endsWith(".lecmo.com")) return "; domain=.lecmo.com";
  return "";
}

/** A stored choice is usable only when its categories and policy version match. */
function parseConsent(raw: string | null): ConsentValue | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const value = parsed as Partial<ConsentValue>;
    if (typeof value.analytics !== "boolean" || typeof value.ads !== "boolean") return null;
    if (value.version !== COOKIE_CONSENT_VERSION) return null;
    return {
      analytics: value.analytics,
      ads: value.ads,
      at: typeof value.at === "string" ? value.at : new Date().toISOString(),
      version: COOKIE_CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

function persist(value: ConsentValue): void {
  const serialized = JSON.stringify(value);
  // An embedded preview may block cookies while localStorage is still usable.
  // Keep the two writes independent so either store can preserve the choice.
  try {
    const encoded = encodeURIComponent(serialized);
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${STORAGE_KEY}=${encoded}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}${cookieDomain()}`;
  } catch {
    /* Cookies are unavailable; still try the independent storage backup. */
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, serialized);
  } catch {
    /* Storage is unavailable; any successful cookie write remains usable. */
  }
}

export function readConsent(): ConsentValue | null {
  if (typeof window === "undefined") return null;
  try {
    const match = document.cookie.match(
      new RegExp("(?:^|;\\s*)" + STORAGE_KEY + "=([^;]*)")
    );
    if (match) {
      const consent = parseConsent(decodeURIComponent(match[1]));
      // A valid cookie is authoritative, including an explicit rejection.
      if (consent) return consent;
    }
  } catch {
    /* Blocked cookies or malformed encoding must not prevent the backup read. */
  }
  try {
    return parseConsent(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function writeConsent(next: { analytics: boolean; ads: boolean }): ConsentValue {
  const value: ConsentValue = {
    analytics: next.analytics,
    ads: next.ads,
    at: new Date().toISOString(),
    version: COOKIE_CONSENT_VERSION,
  };
  if (typeof window !== "undefined") {
    persist(value);
    // Synchronous and before any SDK work: delayed handlers can never miss a
    // withdraw→grant sequence, even if a third-party call below throws.
    noteConsentTransition(value);
    try {
      applyConsentToGtag(value);
    } catch {
      /* a throwing Google SDK must not abort saving or other listeners */
    }
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  }
  return value;
}

/** يبلّغ Google Consent Mode بالحالة الحاليّة (يعمل بعد تحميل gtag أو قبله عبر dataLayer). */
export function applyConsentToGtag(value: ConsentValue): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  w.dataLayer = w.dataLayer || [];
  const params = {
    analytics_storage: value.analytics ? "granted" : "denied",
    ad_storage: value.ads ? "granted" : "denied",
    ad_user_data: value.ads ? "granted" : "denied",
    ad_personalization: value.ads ? "granted" : "denied",
  };
  if (typeof w.gtag === "function") {
    w.gtag("consent", "update", params);
  } else {
    // مهمّ: gtag يتوقّع Arguments object في dataLayer — دفع Array عاديّة
    // لا يُطبّق أمر consent إطلاقاً.
    const dl = w.dataLayer;
    const pushArgs = function (this: void, ..._rest: unknown[]) {
      // eslint-disable-next-line prefer-rest-params
      dl.push(arguments);
    };
    pushArgs("consent", "update", params);
  }

}


export function hasAdsConsent(): boolean {
  return readConsent()?.ads === true;
}

export function hasAnalyticsConsent(): boolean {
  return readConsent()?.analytics === true;
}

/** هل وافق المستخدم على أيّ كوكي اختياري (تحليلات أو تسويق)؟ */
export function hasAnyOptionalConsent(): boolean {
  const c = readConsent();
  return c?.analytics === true || c?.ads === true;
}

